import { FilterCard, CustomDataTable, useApi, Toaster, CustomAddRegionModal, CustomBackdrop, validationMessages } from '../../utils/globalImports';
import { React, useState, useEffect, } from '../../utils/globalImports';
import { ApiResponse } from '../../services/apiService';
import { useConfirmCloseDialog } from '../../contexts/ConfirmCloseDialogContext';

interface Region {
    regionId?: number;
    regionName: string;
    status: boolean;
    description: string;
    createdId?: number;
    updatedId?: number;
    createdAt?: Date;
    updatedAt?: Date;
}

const ClientRegion: React.FC = () => {

    // State to manage the modal visibility
    const [modalOpen, setModalOpen] = useState(false);
    const [regionDatalist, setRegionDatalist] = useState<Region[]>([]);
    const [selectedRegion, setSelectedRegion] = useState<any | null>(null);
    const [regionData, setRegionData] = useState<any[]>([]);
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const [currentStatus, setCurrentStatus] = useState<string[]>(['Active']);
    const [toast, setToast] = useState({ open: false, message: "", type: "success" });
    const [isOn, setIsOn] = useState<boolean>();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

    const { execute, loading } = useApi();
    const { showConfirmCloseDialog } = useConfirmCloseDialog();
    const props = { showClientDropdown: false, title: false, actionText: 'Add Client Region', searchbarText: 'Search for a client region by name, description' };

    const userId = Number(localStorage.getItem("userId"));
    
    useEffect(() => {
        fetchRegions();
    }, []);  // The empty dependency array ensures it only runs once when the component mounts

    // Fetch regions
    const fetchRegions = async () => {
        const { data } = await execute<Region[]>('get', '/Lookup/GetRegions');
        if (data) {
            setRegionDatalist(data);
    
            const regionData = data.map((region) => {
                const { regionId, createdAt, regionName, description, status } = region; 
                const Status = status ? 'Active' : 'Inactive';
    
                return { id: regionId, createdAt, regionId, Name: regionName, description, Status };
            });
    
            setRegionData(regionData);
            const activeRegions = regionData
                .filter((region) => currentStatus.includes(region.Status))
                .sort((a, b) => a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' }));
                setFilteredData(activeRegions);
        }
    };

    // Filter Data
    const handleSearchGridData = (query: string, status: Number) => {
        const numericStatus = Number(status); // Convert status to a number
        const lowerCaseQuery = query.toLowerCase();
    
        // Map statuses to numeric values for filtering
        const statusMapping: Record<string, number> = {
            Active: 1,
            Inactive: 0,
        };
    
        // Ensure that regionData is properly filtered
        const filtered = regionData.filter((item) => {
            const itemStatus = statusMapping[item.Status] ?? -1; // Ensure mapping exists
            const matchesQuery =
                item.Name.toLowerCase().includes(lowerCaseQuery) || //Search in regionName
                (item.description && item.description.toLowerCase().includes(lowerCaseQuery)); //Search in description
    
            const matchesStatus =
                numericStatus === -1 || // If -1 (show all)
                itemStatus === numericStatus; // Match the exact status
    
            return matchesQuery && matchesStatus;
        }).sort((a, b) =>
            a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' })
        );
    
        //Update filteredData with correctly filtered regions
        setFilteredData(filtered);
    
        //Ensure currentStatus is correctly updated
        setCurrentStatus(
            numericStatus === 0
                ? ['Inactive']
                : numericStatus === -1
                    ? ['Active', 'Inactive']
                    : numericStatus === 1
                        ? ['Active']
                        : currentStatus
        );
    };      

    const handleFormSubmit = async (formData: any, setBackendErrors?: (errors: any) => void) => {
        let response: ApiResponse<any>  = { data: null, error: null };
        if (selectedRegion) {
            const updatedRegion = regionDatalist.find((region) => region.regionId === selectedRegion.regionId);
            // If the region is found, update the fields
            if (updatedRegion) {
                // Step 2: Update the fields on the filtered record
                const updatedRegionData = {
                    ...updatedRegion, // Keep the original region data
                    regionName: formData.regionName,
                    description: formData.description,
                    status: formData.status === 'Active' ? true : false, // Update based on Status
                    updatedId: userId,
                };

                response = await execute('put', '/Lookup/UpdateRegion', updatedRegionData);
            }
        } else {

            // Adding a new region
            const region: Region = {
                regionName: formData.regionName,
                description: formData.description,
                status: formData.status === 'Active' ? true : false,
                createdId: userId,
            };

            response = await execute('post', '/Lookup/CreateRegion', region);
        }

        if (response && response.error) {
            if (
              typeof response.error === 'string' &&
              response.error.includes('Region name already exists')
            ) {
              setBackendErrors?.({ regionName: response.error });
              return false; // Don't close modal
            } else {
              setToast({ open: true, message: response.error, type: 'error' });
              return true; // Close modal on other errors
            }
          } else {
            // Submission successful – close the modal
            setToast({ open: true, message: response?.data, type: 'success' });
            setModalOpen(false);
            setSelectedRegion(null);
            handleContentChange(false);
            fetchRegions();
            return true;
          }
    };      

    const handleEdit = (region: any) => {
        setSelectedRegion({
            ...region,
            regionName: region.Name,
            status: region.Status === 'Active' ? 'Active' : 'Inactive'
            }); 
        handleOpenModal(); // Open the modal
    };

    const handleAdd = () => {
        setSelectedRegion(null);
        setModalOpen(true);
    };

    const handleOpenModal = () => {
        setModalOpen(true);
    };

    const handleToggleState = (row: any) => {
        if (row.Status === 'Inactive') {
            setIsOn(false)
        } else {
            setIsOn(true)
        }
        setSelectedRegion(row);
        setDialogOpen(true);
    };

    const handleDialogCancel = () => {
        setDialogOpen(false);
        setSelectedRegion(null); 
    };

    const handleDialogConfirm = async () => {
        if (!selectedRegion) return;
        try {
            // Find the selected region in the list
            const updatedRegion = regionDatalist.find(
                (region) => region.regionId === selectedRegion.regionId
            );
    
            if (updatedRegion) {
                //Toggle the status dynamically
                const newStatus = updatedRegion.status ? false : true;
    
                const updatedRegionData = {
                    ...updatedRegion,
                    status: newStatus, //Toggle between true (Active) and false (Inactive)
                    updatedId: userId,
                };
    
                //Make the API call to update the status
                const response = await execute('put', '/Lookup/UpdateRegion', updatedRegionData);
                if (response?.error) {
                    setToast({ open: true, message: response.error, type: 'error' });
                  } else {
                    setToast({ open: true, message: 'Status updated successfully', type: 'success' });
                  }
            }
    
            //Refresh the region list
            fetchRegions();
            setSelectedRegion(null);
        } catch (error) {
            console.error('Error updating status:', error);
        } finally {
            setDialogOpen(false);
        }
    };    

    const handleContentChange = (state:boolean) => {
    setHasUnsavedChanges(state); 
    };

  const handleClose=()=>{
    if (hasUnsavedChanges) { 
        showConfirmCloseDialog({
          onConfirm: () => { 
            setHasUnsavedChanges(false);  
            setModalOpen(false);       
          },
          onCancel: () => { 
          },
        });
      }
      else{
        setModalOpen(false);
      } 
  }

    return (
        <>
            <FilterCard {...props} handleModalOrNavigate={handleAdd} handleSearch={handleSearchGridData} />
            <CustomAddRegionModal handleContentChange={handleContentChange} open={modalOpen} onClose={handleClose} onSubmit={handleFormSubmit} regionData={selectedRegion} />
            {regionDatalist.length > 0 && (
                <CustomDataTable
                    rows={filteredData}
                    actionText="Client Region"
                    sortOptions={[{ value: "Name", text: "Name" }]}
                    onRowEdit={handleEdit}
                    onToggleClick={handleToggleState}
                    isDialogOpen={dialogOpen}
                    isToggleOn={isOn}
                    handleCancel={handleDialogCancel}
                    handleConfirm={handleDialogConfirm}
                    // gridHeight={'calc(100vh - 500px)'}
                    noResultMessage={validationMessages.common_RecordNotFound("client regions")}
                />)}
            <CustomBackdrop open={loading} />
            <Toaster open={toast.open} message={toast.message} type={toast.type as "success" | "error"} onClose={() => setToast({ ...toast, open: false })} />
        </>
    );
};

export default ClientRegion;